import React from "react";
import { Form } from "react-bootstrap";
import DateInputField from "../Components/DatePicker/DateInputField/DateInputField";
import { Dropdown, DropdownToggleType, MenuItem } from "../Components/Dropdown";
import { CheckOption, OnChange, OnChangeHelper } from "./Question";
import { MultiDropDownQuestion } from "./MultiDropDownQuestion";

interface NestedInputProps {
  config: NestedOptionConfig[];
  readOnly: boolean;
  onChange: OnChange;
  formState?: any;
  disabled?: boolean;
}

export const enum NestedInputType {
  CHECKBOX = "checkbox",
  DATEFIELD = "datefield",
  DROPDOWN = "dropdown",
  DROPDOWN_MULTI = "dropdownMulti",
  RADIO = "radio",
  CUSTOM = "custom",
}

export interface NestedDropdownProps {
  size?: "sm" | "lg";
  type?: DropdownToggleType;
  design?: "primary" | "secondary";
  background?: "light" | "dark";
  itemOnClick?: (item: MenuItem<string | number>) => void;
}

export interface NestedOptionConfig {
  inputType: NestedInputType;
  options?: { value: string | number | boolean; label: string }[];
  label?: string;
  placeholder?: string;
  formPath: string;
  note?: string;
  checkOptions?: CheckOption;
  onChangeHelper?: OnChangeHelper;
  disable?: boolean;
  dropdownProps?: NestedDropdownProps;
  customContent?: JSX.Element;
  formState?: any;
  formSetter?: OnChange;
  subHeader?: string;
}

export interface NestedOptionsConfigMap {
  [option: string]: NestedOptionConfig[];
}

export const NestedInput: React.FC<NestedInputProps> = ({
  config,
  readOnly,
  onChange,
  formState,
  disabled = false,
}) => {
  return (
    <div className="nestedFields">
      {config.map(item => {
        let content;
        if (item.inputType === NestedInputType.CHECKBOX && item.options) {
          content = (
            <Form.Group key={`nestedFields-${item.inputType}`}>
              {item.options.map(option => {
                const { value, label } = option;
                const isChecked = item.checkOptions
                  ? item.checkOptions(value, formState)
                  : formState[item.formPath] === value;
                return (
                  <Form.Check
                    key={`nestedFields-${item.inputType}-${value}`}
                    type="checkbox"
                    label={label}
                    checked={isChecked as boolean}
                    onChange={() => onChange(value, item.formPath, item.onChangeHelper)}
                    disabled={readOnly}
                  />
                );
              })}
            </Form.Group>
          );
        } else if (item.inputType === NestedInputType.RADIO && item.options) {
          content = item.options.map((option, index) => {
            const { value, label } = option;
            const isChecked = formState[item.formPath] === value;
            return (
              <div key={`radioOption${index}`} className="radioOption">
                <Form.Check
                  key={`nestedFields-${item.inputType}-${value}`}
                  type="radio"
                  name={item.label}
                  id={`${item.label}-${value}-radio`}
                  label={label}
                  checked={isChecked}
                  onChange={() => onChange(value, item.formPath, item.onChangeHelper)}
                  disabled={readOnly}
                />
              </div>
            );
          });
        } else if (item.inputType === NestedInputType.DATEFIELD) {
          const currVal = formState[item.formPath];
          const isParseableDate = typeof currVal === "string" && currVal.length === 10;
          content = (
            <DateInputField
              label={null}
              value={isParseableDate ? currVal : ""}
              invalidateDateOrRange={() => false}
              setValue={value => {
                onChange(value, item.formPath, item.onChangeHelper);
              }}
            />
          );
        } else if (item.inputType === NestedInputType.DROPDOWN && item.options) {
          const resolvedOnChange = item.formSetter ? item.formSetter : onChange;
          content = (
            <Dropdown
              value={
                disabled || item.disable
                  ? ""
                  : item.checkOptions
                  ? item.checkOptions()
                  : formState[item.formPath]
              }
              options={item.options}
              onChange={value => {
                resolvedOnChange(value, item.formPath, item.onChangeHelper);
              }}
              {...item.dropdownProps}
              disabled={disabled || item.disable}
              hasFilterBar={false}
            />
          );
        } else if (item.inputType === NestedInputType.DROPDOWN_MULTI && item.options) {
          const resolvedOnChange = item.formSetter ? item.formSetter : onChange;
          content = (
            <MultiDropDownQuestion
              options={item.options}
              onChange={value => {
                resolvedOnChange(value, item.formPath, item.onChangeHelper);
              }}
              checkOptions={item.checkOptions}
              formPath={item.formPath}
              onChangeHelper={item.onChangeHelper}
              formState={formState}
              dropdownProps={item.dropdownProps}
            />
          );
        } else if (item.inputType === NestedInputType.CUSTOM && item.customContent) {
          content = item.customContent;
        }
        return (
          content && (
            <div
              key={`nestedValue${item.label}${item.inputType}${item.formPath}`}
              className={`${item.disable ? "disabled" : ""}`}
            >
              {item.label && <div className="nestConfigLabel">{item.label}</div>}
              {item.subHeader && <div className="nestConfigLabel subheader">{item.subHeader}</div>}
              {content}
              {item.note && <div className="nestedInputNote">{item.note}</div>}
            </div>
          )
        );
      })}
    </div>
  );
};
